import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { History as HistoryIcon, Undo2, CheckCircle2, Loader2, Filter, User as UserIcon, Calendar as CalendarIcon, Search, X, RotateCcw } from "lucide-react";

// Session Z68 — Bulk operations history page. Lists every bulk_revert
// op the Owner has ever fired, surfaces its filter envelope, and offers
// a one-click rollback for ops that haven't been undone yet.

const _fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch (_e) { return iso || ""; }
};

const _summariseFilter = (f) => {
  if (!f) return "no filter";
  const bits = [];
  if (f.actor) bits.push(`actor "${f.actor}"`);
  if (f.property_id) bits.push("one property");
  if (f.action) bits.push(f.action.replace("_", " "));
  if (f.from_date || f.to_date) {
    const range = `${f.from_date || "…"} → ${f.to_date || "…"}`;
    bits.push(range);
  }
  return bits.length ? bits.join(" · ") : "whole audit log";
};

export default function BulkHistory() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(null);
  const [hideRolled, setHideRolled] = useState(false);
  const [actor, setActor] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [envelope, setEnvelope] = useState("");

  const buildParams = () => {
    const params = { limit: 100 };
    if (actor.trim()) params.actor = actor.trim();
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (envelope.trim()) params.envelope = envelope.trim();
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cleaning-cost-history/bulk-ops", { params: buildParams() });
      setOps(data?.items || []);
    } catch (err) {
      toast.error(apiError(err, "Couldn't load bulk history"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const resetFilters = () => {
    setActor(""); setFromDate(""); setToDate(""); setEnvelope("");
    setTimeout(load, 0);
  };

  const actorOptions = useMemo(() => {
    const set = new Set();
    ops.forEach((o) => { if (o.actor_name) set.add(o.actor_name); });
    return Array.from(set).sort();
  }, [ops]);

  const anyFilter = Boolean(actor || fromDate || toDate || envelope);

  const rollback = async (op) => {
    if (!window.confirm(
      `Roll back this bulk op?\n\n`
      + `${op.property_count} propert${op.property_count === 1 ? "y" : "ies"} will return to the value they had just after the original bulk revert.\n\n`
      + `Fired ${_fmtDate(op.at)} by ${op.actor_name || "unknown"}.`
    )) return;
    setRolling(op.bulk_op_id);
    try {
      const { data } = await api.post(`/cleaning-cost-history/bulk-rollback/${op.bulk_op_id}`);
      toast.success(`Rolled back ${data.rolled_back} propert${data.rolled_back === 1 ? "y" : "ies"}`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Rollback failed"));
    } finally {
      setRolling(null);
    }
  };

  const visible = hideRolled ? ops.filter((o) => !o.rolled_back) : ops;
  const activeCount = ops.filter((o) => !o.rolled_back).length;

  return (
    <Layout
      title="Bulk operations history"
      subtitle="Every bulk revert on record. Undo one from last week without hunting through the audit log."
    >
      <section className="ns-card p-6" data-testid="bulk-history-page">
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div className="min-w-0">
            <div className="ns-label">Bulk history</div>
            <div className="font-serif text-xl mt-1">
              {ops.length} operation{ops.length === 1 ? "" : "s"} on record
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {activeCount} undo-able · {ops.length - activeCount} already rolled back
            </div>
          </div>
          <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
            <HistoryIcon className="w-5 h-5" />
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideRolled}
              onChange={(e) => setHideRolled(e.target.checked)}
              data-testid="bulk-history-hide-rolled"
              className="w-4 h-4 accent-teal-600"
            />
            Hide already-rolled-back ops
          </label>
          <Button
            type="button"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="bulk-history-refresh"
            className="rounded-full h-9 px-4 gap-2 ml-auto"
          >
            <Filter className="w-3 h-3" />
            Refresh
          </Button>
        </div>

        {/* Task 4 — actor / date / envelope filter toolbar. All params hit the
            new query support on /cleaning-cost-history/bulk-ops so operators
            can scope a search without hand-crafting URLs. */}
        <div className="flex items-end gap-2 mb-5 flex-wrap bg-stone-50 border border-slate-100 rounded-xl p-3" data-testid="bulk-history-filters">
          <div className="flex flex-col min-w-[180px]">
            <label className="ns-label mb-1" htmlFor="bulk-history-actor">Actor</label>
            <input
              id="bulk-history-actor"
              list="bulk-history-actor-list"
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              placeholder="Any owner / admin"
              data-testid="bulk-history-filter-actor"
              className="h-9 bg-white border border-slate-200 rounded-md px-2 text-sm"
            />
            <datalist id="bulk-history-actor-list">
              {actorOptions.map((a) => (<option key={a} value={a} />))}
            </datalist>
          </div>
          <div className="flex flex-col">
            <label className="ns-label mb-1" htmlFor="bulk-history-from">From</label>
            <input
              id="bulk-history-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="bulk-history-filter-from"
              className="h-9 bg-white border border-slate-200 rounded-md px-2 text-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="ns-label mb-1" htmlFor="bulk-history-to">To</label>
            <input
              id="bulk-history-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="bulk-history-filter-to"
              className="h-9 bg-white border border-slate-200 rounded-md px-2 text-sm"
            />
          </div>
          <div className="flex flex-col flex-1 min-w-[180px]">
            <label className="ns-label mb-1" htmlFor="bulk-history-envelope">Envelope keyword</label>
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                id="bulk-history-envelope"
                value={envelope}
                onChange={(e) => setEnvelope(e.target.value)}
                placeholder="e.g. direct_edit or property id"
                data-testid="bulk-history-filter-envelope"
                className="h-9 bg-white border border-slate-200 rounded-md pl-7 pr-2 text-sm w-full"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="bulk-history-filter-apply"
              className="rounded-full h-9 px-4 gap-2 bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Search className="w-3 h-3" />
              Apply
            </Button>
            {anyFilter && (
              <Button
                type="button"
                variant="outline"
                onClick={resetFilters}
                data-testid="bulk-history-filter-reset"
                className="rounded-full h-9 px-3 gap-2"
              >
                <X className="w-3 h-3" />
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto -mx-6">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-slate-500">
              <tr>
                <th className="ns-label px-6 py-3 text-left">When</th>
                <th className="ns-label px-6 py-3 text-left">Actor</th>
                <th className="ns-label px-6 py-3 text-right">Properties</th>
                <th className="ns-label px-6 py-3 text-left">Filter envelope</th>
                <th className="ns-label px-6 py-3 text-right">Status</th>
                <th className="ns-label px-6 py-3 text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Loading…</td></tr>
              )}
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={6} data-testid="bulk-history-empty" className="text-center py-10 text-slate-400 italic">
                    {hideRolled && ops.length > 0
                      ? "Every op has been rolled back. Untick the hide filter to see them."
                      : "No bulk operations yet. Fire one from the Cost audit page to see it here."}
                  </td>
                </tr>
              )}
              {visible.map((op, i) => (
                <tr key={op.bulk_op_id} data-testid={`bulk-history-row-${i}`} className="border-t border-slate-100 hover:bg-stone-50/40">
                  <td className="px-6 py-3 text-slate-600 text-[13px] whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5">
                      <CalendarIcon className="w-3 h-3 text-slate-400" />
                      {_fmtDate(op.at)}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="inline-flex items-center gap-1.5 text-slate-700">
                      <UserIcon className="w-3 h-3 text-slate-400" />
                      {op.actor_name || <span className="italic text-slate-400">unknown</span>}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-right tabular-nums font-semibold text-slate-900">
                    {op.property_count}
                  </td>
                  <td className="px-6 py-3 text-slate-500 text-[12px] max-w-md truncate">
                    {_summariseFilter(op.bulk_filter)}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {op.rolled_back ? (
                      <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid={`bulk-history-badge-rolledback-${i}`}>
                        <CheckCircle2 className="w-3 h-3" />
                        rolled back
                      </span>
                    ) : op.restored ? (
                      <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" title="This op was rolled back and then restored (undo-of-undo)" data-testid={`bulk-history-badge-restored-${i}`}>
                        <RotateCcw className="w-3 h-3" />
                        restored
                      </span>
                    ) : (
                      <span className="inline-flex items-center bg-amber-50 text-amber-700 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid={`bulk-history-badge-active-${i}`}>
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {isOwner && !op.rolled_back && (
                      <button
                        type="button"
                        data-testid={`bulk-history-rollback-${i}`}
                        onClick={() => rollback(op)}
                        disabled={rolling === op.bulk_op_id}
                        className="inline-flex items-center gap-1 text-[11px] rounded-full border border-red-200 bg-white text-red-700 hover:bg-red-50 px-3 py-1 disabled:opacity-40"
                      >
                        {rolling === op.bulk_op_id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Undo2 className="w-3 h-3" />
                        }
                        {rolling === op.bulk_op_id ? "Rolling back…" : "Roll back"}
                      </button>
                    )}
                    {!isOwner && !op.rolled_back && (
                      <span className="text-[10px] italic text-slate-400">owner only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}
